import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AtlasView, { LENS_COLORS, LENS_LABELS } from "@/components/AtlasView";
import PassportCard from "@/components/PassportCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, SpinnerGap, Globe, LockSimple, LinkSimple, Check, Trash, X,
  MapPin, House, AirplaneTilt, PaperPlaneTilt, GlobeHemisphereWest, FlagBanner, MapTrifold, Buildings,
  NavigationArrow, Stamp, FileArrowUp, Sparkle, CalendarBlank,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";

const SITES = [
  { v: "socialpulse", l: "SocialPulse" }, { v: "indianfamilytree", l: "FamilyTree" },
  { v: "alumnipulse", l: "AlumniPulse" }, { v: "travelerpulse", l: "TravelerPulse" },
  { v: "foodpulse", l: "FoodPulse" }, { v: "naturepulse", l: "NaturePulse" },
];
const LENS_BTN = [
  { v: "visited", l: "Visited", icon: MapPin }, { v: "lived", l: "Lived", icon: House },
  { v: "next", l: "Next", icon: AirplaneTilt },
];
const GRANULARITY = [
  { v: "countries", l: "Countries", icon: FlagBanner },
  { v: "regions", l: "Regions", icon: MapTrifold },
  { v: "cities", l: "Cities", icon: Buildings },
];
const flag = (cc) => (cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : "🏳️");

export default function AtlasMap() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [lensFilter, setLensFilter] = useState("all");
  const [granularity, setGranularity] = useState("countries");
  const [pinMode, setPinMode] = useState(false);
  const [pinDraft, setPinDraft] = useState(null); // {lat, lon}
  const [pinLabel, setPinLabel] = useState("");
  const [pinLens, setPinLens] = useState("visited");
  const [stampResult, setStampResult] = useState(null); // {loading}|{country,country_code,confidence,meta}
  const [stampLens, setStampLens] = useState("visited");
  const [listPreview, setListPreview] = useState(null); // {matched, unmatched, unmatched_count}
  const [listBusy, setListBusy] = useState(false);
  const [describing, setDescribing] = useState(false);
  const stampInputRef = useRef();
  const listInputRef = useRef();
  const [sel, setSel] = useState(null); // {a2, name, info}
  const [pushSite, setPushSite] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get(`/atlas/maps/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load map"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const map = data?.map;
  const countries = data?.countries || {};

  const setLens = async (lens) => {
    if (!sel) return;
    try {
      await api.post(`/atlas/maps/${id}/place`, { country_code: sel.a2, lens });
      toast.success(`${sel.name} marked as ${LENS_LABELS[lens]}`);
      setSel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const clearLens = async () => {
    if (!sel) return;
    try {
      await api.delete(`/atlas/maps/${id}/place/${sel.a2}`);
      toast.success(`${sel.name} cleared`); setSel(null); load();
    } catch (e) { toast.error("Could not clear"); }
  };

  const toggleVisibility = async () => {
    const next = map.visibility === "public" ? "private" : "public";
    try {
      await api.patch(`/atlas/maps/${id}`, { visibility: next });
      toast.success(next === "public" ? "Map is now public" : "Map is now private");
      load();
    } catch (e) { toast.error("Could not update"); }
  };

  const copyShare = () => {
    const url = `${window.location.origin}/atlas/p/${map.share_slug}`;
    navigator.clipboard?.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
    toast.success("Share link copied");
  };

  const del = async () => {
    if (!window.confirm("Delete this map? Your photos are not affected.")) return;
    await api.delete(`/atlas/maps/${id}`);
    toast.success("Map deleted"); nav("/atlas");
  };

  const pushToSite = async (countryCode = null) => {
    if (!pushSite) { toast.error("Pick a destination site"); return; }
    try {
      const { data: r } = await api.post(`/atlas/maps/${id}/push`, { site: pushSite, country_code: countryCode });
      toast.success(`Sent ${r.updated} item(s) to ${pushSite}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Push failed"); }
  };

  if (!data) return <div className="grid h-[60vh] place-items-center"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" /></div>;

  const selCount = sel ? (countries[sel.a2]?.count || 0) : 0;

  const savePin = async () => {
    if (!pinDraft) return;
    try {
      await api.post(`/atlas/maps/${id}/pins`, { lat: pinDraft.lat, lon: pinDraft.lon, label: pinLabel.trim(), lens: pinLens });
      toast.success(`Pinned ${pinLabel.trim() || "place"}`);
      setPinDraft(null); setPinLabel(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add pin"); }
  };
  const deletePin = async (pinId) => {
    try { await api.delete(`/atlas/maps/${id}/pins/${pinId}`); toast.success("Pin removed"); load(); }
    catch { toast.error("Could not remove pin"); }
  };

  const pinMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device."); return; }
    toast.info("Getting your location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setPinMode(false); setPinLabel(""); setPinLens("visited"); setPinDraft({ lat: pos.coords.latitude, lon: pos.coords.longitude }); },
      () => toast.error("Couldn't get your location. Please allow access."),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const importList = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("lens", "visited"); fd.append("preview", "true");
    toast.info("Reading your list…");
    try {
      const { data } = await api.post(`/atlas/maps/${id}/import-list`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.matched_count && !data.unmatched_count) { toast.error("No places found in that file"); return; }
      setListPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read that file"); }
  };

  const confirmList = async () => {
    if (!listPreview?.matched?.length) { setListPreview(null); return; }
    setListBusy(true);
    try {
      const { data } = await api.post(`/atlas/maps/${id}/apply-places`, { places: listPreview.matched });
      toast.success(`Added ${data.added} countr${data.added === 1 ? "y" : "ies"} to your map`);
      setListPreview(null); load();
    } catch { toast.error("Could not apply"); }
    setListBusy(false);
  };

  const describeCountry = async () => {
    if (!sel) return;
    setDescribing(true);
    try {
      await api.post(`/atlas/maps/${id}/describe`, { country_code: sel.a2, lens: countries[sel.a2]?.lens || "visited" });
      await load();
    } catch { toast.error("Could not generate a description"); }
    setDescribing(false);
  };

  const scanStamp = async (f) => {
    if (!f) return;
    setStampResult({ loading: true });
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/atlas/maps/${id}/scan-stamp`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStampLens("visited"); setStampResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); setStampResult(null); }
  };

  const confirmStamp = async () => {
    if (!stampResult?.country_code) return;
    try {
      await api.post(`/atlas/maps/${id}/place`, { country_code: stampResult.country_code, lens: stampLens });
      toast.success(`${stampResult.meta?.name || stampResult.country} added to your map`);
      setStampResult(null); load();
    } catch { toast.error("Could not add to map"); }
  };

  return (
    <div className="mx-auto max-w-[1200px]">
      <button onClick={() => nav("/atlas")} data-testid="atlas-back" className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-[#F27A54]">
        <ArrowLeft size={16} weight="bold" /> All maps
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-black tracking-tight sm:text-3xl">{map.name}</h1>
          {map.description && <p className="mt-1 text-sm text-muted-foreground">{map.description}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="atlas-visibility-toggle" variant="outline" onClick={toggleVisibility}
            className="h-9 gap-1.5 rounded-md">
            {map.visibility === "public" ? <Globe size={15} weight="fill" className="text-emerald-600" /> : <LockSimple size={15} weight="fill" />}
            {map.visibility === "public" ? "Public" : "Private"}
          </Button>
          {map.visibility === "public" && (
            <>
              <Button data-testid="atlas-share-btn" variant="outline" onClick={copyShare} className="h-9 gap-1.5 rounded-md">
                {copied ? <Check size={15} weight="bold" className="text-emerald-600" /> : <LinkSimple size={15} weight="bold" />}
                {copied ? "Copied" : "Copy link"}
              </Button>
              <ShareButton id={map.share_slug} type="atlas" title={map.name} variant="button" />
            </>
          )}
          <Button data-testid="atlas-delete-btn" variant="outline" onClick={del} className="h-9 w-9 rounded-md p-0 text-red-500 hover:bg-red-50"><Trash size={15} weight="bold" /></Button>
        </div>
      </div>

      {/* Granularity + lens filter */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="mr-1 flex rounded-full bg-zinc-100 p-1" data-testid="granularity-tabs">
          {GRANULARITY.map((g) => (
            <button key={g.v} data-testid={`granularity-${g.v}`} onClick={() => setGranularity(g.v)}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${granularity === g.v ? "bg-white text-[#2C2724] shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}>
              <g.icon size={13} weight="fill" /> {g.l}
            </button>
          ))}
        </div>
        <span className="mx-1 h-5 w-px bg-zinc-200" />
        <button data-testid="lens-all" onClick={() => setLensFilter("all")}
          className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${lensFilter === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>All lenses</button>
        {LENS_BTN.map((b) => (
          <button key={b.v} data-testid={`lens-${b.v}`} onClick={() => setLensFilter(b.v)}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${lensFilter === b.v ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            style={lensFilter === b.v ? { background: LENS_COLORS[b.v] } : {}}>
            <b.icon size={12} weight="fill" /> {b.l}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-zinc-200" />
        <button data-testid="pin-mode-toggle" onClick={() => { setPinMode((v) => !v); setPinDraft(null); }}
          className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${pinMode ? "bg-[#2D6A4F] text-white" : "border border-[#2D6A4F]/40 text-[#2D6A4F] hover:bg-emerald-50"}`}>
          <MapPin size={13} weight="fill" /> {pinMode ? "Tap map to pin…" : "Add place pin"}
        </button>
      </div>

      {pinMode && (
        <p className="mt-2 text-xs font-semibold text-[#2D6A4F]" data-testid="pin-mode-hint">
          Pin places you visited even without photos — tap anywhere on the map to drop a pin.
        </p>
      )}

      <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_340px]">
        <div>
          <AtlasView countries={countries} points={data.points} lensFilter={lensFilter} granularity={granularity}
            manualPins={map.manual_pins || []} pinMode={pinMode}
            onMapClick={(lat, lon) => setPinDraft({ lat, lon })} onDeletePin={deletePin}
            editable onCountryClick={(a2, name, info) => { if (!pinMode) setSel({ a2, name, info }); }} height="62vh" />
          <p className="mt-2 text-xs text-muted-foreground">
            {granularity === "countries" ? "Flags mark every country you've visited. Tap a country to mark it visited, lived or next."
              : granularity === "regions" ? "Grouped by region/state. Tap a bubble to preview photos there."
              : "Grouped by city, with every photo & video pin. Tap a bubble to preview."}
          </p>
        </div>

        <div className="space-y-4">
          <PassportCard stats={data.stats} />

          {/* Cross-site push */}
          <div className="rounded-xl border border-border p-4" data-testid="atlas-push-panel">
            <div className="flex items-center gap-2">
              <PaperPlaneTilt size={18} weight="fill" className="text-[#F27A54]" />
              <h3 className="font-display text-sm font-bold">Feed to a sister site</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Make this map's located photos &amp; videos available to another Pulse site's Media API.</p>
            <Select value={pushSite} onValueChange={setPushSite}>
              <SelectTrigger data-testid="push-site-select" className="mt-3 h-9 rounded-md"><SelectValue placeholder="Choose a site…" /></SelectTrigger>
              <SelectContent>{SITES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="push-all-btn" onClick={() => pushToSite(null)} disabled={!pushSite}
              className="mt-2 h-9 w-full gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]">
              <PaperPlaneTilt size={14} weight="fill" /> Push all located media
            </Button>
          </div>

          {/* Add places without photos */}
          <div className="rounded-xl border border-[#E5DFD3] bg-white p-4">
            <div className="flex items-center gap-2">
              <MapPin size={18} weight="fill" className="text-[#2D6A4F]" />
              <h3 className="font-display text-sm font-bold">Add places to your map</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Been somewhere but have no photos? Add it here.</p>
            <div className="mt-3 space-y-2">
              <button data-testid="pin-my-location" onClick={pinMyLocation}
                className="flex w-full items-center gap-2 rounded-md border border-[#E5DFD3] px-3 py-2 text-xs font-bold hover:border-[#2D6A4F]">
                <NavigationArrow size={15} weight="fill" className="text-[#2D6A4F]" /> Pin my current location
              </button>
              <button data-testid="scan-stamp-btn" onClick={() => stampInputRef.current?.click()}
                className="flex w-full items-center gap-2 rounded-md border border-[#E5DFD3] px-3 py-2 text-xs font-bold hover:border-[#2D6A4F]">
                <Stamp size={15} weight="fill" className="text-[#2D6A4F]" /> Scan a passport stamp
              </button>
              <button data-testid="import-list-btn" onClick={() => listInputRef.current?.click()}
                className="flex w-full items-center gap-2 rounded-md border border-[#E5DFD3] px-3 py-2 text-xs font-bold hover:border-[#2D6A4F]">
                <FileArrowUp size={15} weight="fill" className="text-[#2D6A4F]" /> Import a list (JSON/CSV/Excel)
              </button>
            </div>
            <input ref={stampInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="stamp-input"
              onChange={(e) => { scanStamp(e.target.files?.[0]); e.target.value = ""; }} />
            <input ref={listInputRef} type="file" accept=".json,.csv,.xlsx,.xls" className="hidden" data-testid="list-input"
              onChange={(e) => { importList(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        </div>
      </div>

      {/* Country bottom sheet */}
      {sel && (
        <div className="fixed inset-0 z-[1000] flex items-end justify-center sm:items-center" data-testid="country-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-md rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-[10px] text-[#F27A54]">Country</p>
                <h3 className="font-display text-xl font-black">{sel.name}</h3>
                <p className="text-xs text-muted-foreground">{selCount} photo{selCount === 1 ? "" : "s"}/video here{countries[sel.a2]?.lens ? ` · currently ${LENS_LABELS[countries[sel.a2].lens]}` : ""}</p>
              </div>
              <button onClick={() => setSel(null)} className="grid h-8 w-8 place-items-center rounded-md hover:bg-zinc-100"><X size={18} weight="bold" /></button>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {LENS_BTN.map((b) => (
                <button key={b.v} data-testid={`set-lens-${b.v}`} onClick={() => setLens(b.v)}
                  className="flex flex-col items-center gap-1 rounded-lg border border-border py-3 text-xs font-bold text-white transition-transform hover:-translate-y-0.5"
                  style={{ background: LENS_COLORS[b.v] }}>
                  <b.icon size={18} weight="fill" /> {b.l}
                </button>
              ))}
            </div>

            {countries[sel.a2]?.lens && (
              <div className="mt-3 rounded-lg border border-border bg-[#FAF8F5] p-3" data-testid="country-detail">
                {countries[sel.a2]?.visited_date && (
                  <p className="flex items-center gap-1.5 text-xs font-bold text-[#2D6A4F]" data-testid="country-visited-date">
                    <CalendarBlank size={13} weight="fill" /> Visited {countries[sel.a2].visited_date}
                  </p>
                )}
                {countries[sel.a2]?.description ? (
                  <>
                    <p className="mt-1 text-sm leading-relaxed text-zinc-700" data-testid="country-description">{countries[sel.a2].description}</p>
                    <button onClick={describeCountry} disabled={describing} data-testid="regenerate-description"
                      className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-zinc-600 hover:text-[#F27A54]">
                      {describing ? <SpinnerGap size={11} className="animate-spin" /> : <Sparkle size={11} weight="fill" />} Regenerate
                    </button>
                  </>
                ) : (
                  <button onClick={describeCountry} disabled={describing} data-testid="describe-country"
                    className="flex items-center gap-1.5 text-sm font-bold text-[#F27A54] disabled:opacity-60">
                    {describing ? <SpinnerGap size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />} Add an AI description
                  </button>
                )}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <Button variant="outline" onClick={clearLens} data-testid="clear-lens" className="h-9 flex-1 rounded-md">Clear mark</Button>
              <Button onClick={() => { pushToSite(sel.a2); }} disabled={!pushSite} data-testid="push-country"
                className="h-9 flex-1 gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]">
                <PaperPlaneTilt size={13} weight="fill" /> {pushSite ? `Push to ${pushSite}` : "Pick a site first"}
              </Button>
            </div>
            <a href={`/place/country/${encodeURIComponent(sel.name)}`} target="_blank" rel="noreferrer"
              data-testid="view-place-page"
              className="mt-3 flex items-center justify-center gap-1.5 rounded-md border border-[#F27A54] py-2 text-sm font-bold text-[#F27A54] transition-colors hover:bg-orange-50">
              <GlobeHemisphereWest size={15} weight="fill" /> Open {sel.name} place page
            </a>
          </div>
        </div>
      )}

      {pinDraft && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-black/40 p-4" data-testid="pin-draft-dialog" onClick={() => setPinDraft(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-black">Pin this place</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{pinDraft.lat.toFixed(3)}, {pinDraft.lon.toFixed(3)}</p>
            <label className="overline mt-3 block text-[10px] text-muted-foreground">Place name</label>
            <input data-testid="pin-label-input" value={pinLabel} onChange={(e) => setPinLabel(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") savePin(); }} placeholder="e.g. Machu Picchu"
              className="mt-1 h-10 w-full rounded-md border border-border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
            <div className="mt-3 grid grid-cols-3 gap-2">
              {LENS_BTN.map((b) => (
                <button key={b.v} data-testid={`pin-lens-${b.v}`} onClick={() => setPinLens(b.v)}
                  className={`flex flex-col items-center gap-1 rounded-lg border py-2.5 text-xs font-bold transition-colors ${pinLens === b.v ? "text-white" : "border-border text-zinc-600"}`}
                  style={pinLens === b.v ? { background: LENS_COLORS[b.v], borderColor: LENS_COLORS[b.v] } : {}}>
                  <b.icon size={16} weight="fill" /> {b.l}
                </button>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" onClick={() => setPinDraft(null)} className="h-10 flex-1 rounded-full">Cancel</Button>
              <Button data-testid="pin-save" onClick={savePin} className="h-10 flex-1 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245840]">Add pin</Button>
            </div>
          </div>
        </div>
      )}
      {stampResult && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-black/40 p-4" data-testid="stamp-result-dialog" onClick={() => setStampResult(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {stampResult.loading ? (
              <div className="grid place-items-center py-8">
                <SpinnerGap size={30} className="animate-spin text-[#2D6A4F]" />
                <p className="mt-3 text-sm font-semibold text-muted-foreground">Reading the stamp…</p>
              </div>
            ) : stampResult.country_code ? (
              <>
                <div className="flex items-center gap-2 text-[#2D6A4F]"><Stamp size={18} weight="fill" /><p className="overline text-[10px]">Stamp detected</p></div>
                <h3 className="mt-1 font-display text-2xl font-black">{stampResult.meta?.name || stampResult.country}</h3>
                <p className="text-xs text-muted-foreground">
                  {stampResult.city ? `${stampResult.city} · ` : ""}{stampResult.date || ""}
                  {typeof stampResult.confidence === "number" ? ` · ${Math.round(stampResult.confidence * 100)}% sure` : ""}
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {LENS_BTN.map((b) => (
                    <button key={b.v} data-testid={`stamp-lens-${b.v}`} onClick={() => setStampLens(b.v)}
                      className={`flex flex-col items-center gap-1 rounded-lg border py-2.5 text-xs font-bold transition-colors ${stampLens === b.v ? "text-white" : "border-border text-zinc-600"}`}
                      style={stampLens === b.v ? { background: LENS_COLORS[b.v], borderColor: LENS_COLORS[b.v] } : {}}>
                      <b.icon size={16} weight="fill" /> {b.l}
                    </button>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" onClick={() => setStampResult(null)} className="h-10 flex-1 rounded-full">Cancel</Button>
                  <Button data-testid="stamp-confirm" onClick={confirmStamp} className="h-10 flex-1 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245840]">Add to map</Button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <p className="font-display text-lg font-bold">Couldn't read that stamp</p>
                <p className="mt-1 text-sm text-muted-foreground">Try a clearer, well-lit photo of a single stamp.</p>
                <Button onClick={() => setStampResult(null)} className="mt-4 h-10 rounded-full bg-zinc-900 px-6 text-white">Close</Button>
              </div>
            )}
          </div>
        </div>
      )}
      {listPreview && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-black/40 p-4" data-testid="list-preview-dialog" onClick={() => setListPreview(null)}>
          <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-[#2D6A4F]"><FileArrowUp size={18} weight="fill" /><p className="overline text-[10px]">List preview</p></div>
            <h3 className="mt-1 font-display text-xl font-black">
              {listPreview.matched_count} recognised{listPreview.unmatched_count ? ` · ${listPreview.unmatched_count} skipped` : ""}
            </h3>
            <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
              {listPreview.matched.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="list-matched">
                  {listPreview.matched.map((m) => (
                    <span key={m.country_code} className="flex items-center gap-1 rounded-full border border-[#E5DFD3] bg-emerald-50 px-2.5 py-1 text-xs font-semibold">
                      <span>{flag(m.country_code)}</span>{m.name}
                      {m.visited_date && <span className="text-[10px] font-medium text-[#2D6A4F]">· {m.visited_date}</span>}
                      <span className="rounded px-1 text-[9px] font-bold uppercase text-white" style={{ background: LENS_COLORS[m.lens] }}>{m.lens}</span>
                    </span>
                  ))}
                </div>
              )}
              {listPreview.unmatched?.length > 0 && (
                <div className="mt-4" data-testid="list-unmatched">
                  <p className="overline text-[10px] text-muted-foreground">Not recognised (skipped)</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {listPreview.unmatched.map((u, i) => (
                      <span key={i} className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs text-zinc-600 line-through">{u}</span>
                    ))}
                    {listPreview.unmatched_count > listPreview.unmatched.length && <span className="px-1 py-1 text-xs text-zinc-400">+{listPreview.unmatched_count - listPreview.unmatched.length} more</span>}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" data-testid="list-cancel" onClick={() => setListPreview(null)} className="h-10 flex-1 rounded-full">Cancel</Button>
              <Button data-testid="list-confirm" onClick={confirmList} disabled={listBusy || !listPreview.matched_count}
                className="h-10 flex-1 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245840]">
                {listBusy ? <SpinnerGap size={16} className="animate-spin" /> : `Add ${listPreview.matched_count} to map`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
